import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getCookie } from "../../utility/getCookie";
import {
    BankDetailsRequest,
    CustomerKycRequest,
    GlobalBankDetailsRequest,
    GlobalKycRequest,
    GlobalKycResponseData,
    GlobalNomineeDetailsRequest,
    KycResponseData,
    KycState,
    KycUploadResponse,
    NomineeDetailsRequest,
    MyPaymentsRequest,
    MyPaymentsResponse,
    RefreshKycPaymentResponse,
    UnifiedKycRequest,
    KycDraftResponse,
} from "./types";
import { uploadFile } from "../../utility/fileUpload";
import { generateUUID } from "../../utility/generateUUID";

// KYC is served by the Java service (dev1api); other modules stay on the NestJS base.
const API_URL = `${process.env.REACT_APP_KYC_API_BASE_URL || process.env.REACT_APP_API_BASE_URL}/api/v1`;

// Helper to create authorized axios instance
const getAuthHeaders = () => {
    const token = localStorage.getItem("access_token") || getCookie("access_token");
    const idempotencyKey = generateUUID();
    return {
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "x-idempotency-key": idempotencyKey,
        },
    };
};

// Async Thunks

export const uploadKycDocument = createAsyncThunk(
    "customerKyc/uploadDocument",
    async (
        {
            file,
            documentType,
            documentNumber,
        }: { file: File; documentType: string; documentNumber: string },
        { rejectWithValue }
    ) => {
        try {
            const response = await uploadFile(file, {
                description: `${documentType} - ${documentNumber}`,
                folder: 'kyc'
            });

            if (!response.success) {
                return rejectWithValue(response.message || "Upload failed");
            }

            return {
                fileUrl: response.data.url,
                fileName: response.data.fileName,
                mimeType: response.data.mimeType,
                size: response.data.size,
                sellerId: "65fd0a44a3b09e12fcd812e1",
                documentType,
                documentNumber
            };
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Document upload failed"
            );
        }
    }
);

export const submitCustomerKyc = createAsyncThunk(
    "customerKyc/submitCustomerKyc",
    async (data: CustomerKycRequest, { rejectWithValue }) => {
        try {
            const response = await axios.post<KycResponseData>(
                `${API_URL}/kyc/customer`,
                data,
                getAuthHeaders()
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "KYC submission failed"
            );
        }
    }
);

export const submitBankDetails = createAsyncThunk(
    "customerKyc/submitBankDetails",
    async (data: BankDetailsRequest, { rejectWithValue }) => {
        try {
            const response = await axios.post<KycResponseData>(
                `${API_URL}/kyc/customer/bank`,
                data,
                getAuthHeaders()
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Bank details submission failed"
            );
        }
    }
);

export const submitNomineeDetails = createAsyncThunk(
    "customerKyc/submitNomineeDetails",
    async (data: NomineeDetailsRequest, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                `${API_URL}/kyc/customer/nominee`,
                data,
                getAuthHeaders()
            );
            return response.data?.data ?? response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Nominee details submission failed"
            );
        }
    }
);

export const getKycDetails = createAsyncThunk(
    "customerKyc/getKycDetails",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                `${API_URL}/kyc/customer`,
                getAuthHeaders()
            );
            const payload = response.data;
            return (payload?.data || payload) as KycResponseData;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch KYC details"
            );
        }
    }
);

export const submitGlobalCustomerKyc = createAsyncThunk(
    "customerKyc/submitGlobalCustomerKyc",
    async (data: GlobalKycRequest, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                `${API_URL}/kyc/customer/global`,
                data,
                getAuthHeaders()
            );
            return response.data?.data ?? response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Global KYC submission failed"
            );
        }
    }
);

export const submitGlobalBankDetails = createAsyncThunk(
    "customerKyc/submitGlobalBankDetails",
    async (data: GlobalBankDetailsRequest, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                `${API_URL}/kyc/customer/global/bank`,
                data,
                getAuthHeaders()
            );
            return response.data?.data ?? response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Global Bank details submission failed"
            );
        }
    }
);

export const submitGlobalNomineeDetails = createAsyncThunk(
    "customerKyc/submitGlobalNomineeDetails",
    async (data: GlobalNomineeDetailsRequest, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                `${API_URL}/kyc/customer/global/nominee`,
                data,
                getAuthHeaders()
            );
            return response.data?.data ?? response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Global Nominee details submission failed"
            );
        }
    }
);

export const getGlobalKycDetails = createAsyncThunk(
    "customerKyc/getGlobalKycDetails",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get<GlobalKycResponseData>(
                `${API_URL}/kyc/customer/global`,
                getAuthHeaders()
            );
            // Java wraps the {kyc,bank,nominee} map inside `data`.
            return (response.data as any)?.data ?? response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch Global KYC details"
            );
        }
    }
);

export const submitUnifiedKyc = createAsyncThunk(
    "customerKyc/submitUnifiedKyc",
    async (data: UnifiedKycRequest, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                `${API_URL}/kyc/customer`,
                data,
                getAuthHeaders()
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to submit KYC"
            );
        }
    }
);

export const kycCheckout = createAsyncThunk(
    "customerKyc/kycCheckout",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                `${API_URL}/kyc/customer/checkout`,
                data,
                getAuthHeaders()
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch KYC checkout preview"
            );
        }
    }
);

export const submitUnifiedKycWithVerification = createAsyncThunk(
    "customerKyc/submitUnifiedKycWithVerification",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                `${API_URL}/kyc/customer/submit-unified-verification`,
                data,
                getAuthHeaders()
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to submit KYC verification"
            );
        }
    }
);

export const getMyPayments = createAsyncThunk(
    "customerKyc/getMyPayments",
    async (params: MyPaymentsRequest, { rejectWithValue }) => {
        try {
            const response = await axios.get<MyPaymentsResponse>(
                `${API_URL}/kyc/charges/my-payments`,
                {
                    ...getAuthHeaders(),
                    params,
                }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch payment history"
            );
        }
    }
);

export const refreshKycPaymentStatus = createAsyncThunk(
    "customerKyc/refreshPaymentStatus",
    async (paymentId: string, { rejectWithValue }) => {
        try {
            const response = await axios.get<RefreshKycPaymentResponse>(
                `${API_URL}/kyc/customer/payment/refresh/${paymentId}`,
                getAuthHeaders()
            );
            // Java returns {paymentId, paymentStatus, caseId, gatewayOrderId} inside `data`;
            // pages read `data.status` — alias it here.
            const body = response.data as any;
            const d = body?.data || body || {};
            return {
                success: true,
                data: { ...d, status: d.paymentStatus || d.status },
            } as RefreshKycPaymentResponse;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to refresh payment status"
            );
        }
    }
);

export const saveKycDraft = createAsyncThunk(
    "customerKyc/saveKycDraft",
    async (data: Record<string, any>, { rejectWithValue }) => {
        try {
            const response = await axios.post<KycDraftResponse>(
                `${API_URL}/kyc/customer/draft`,
                data,
                getAuthHeaders()
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to save KYC draft"
            );
        }
    }
);

export const getKycDraft = createAsyncThunk(
    "customerKyc/getKycDraft",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get<KycDraftResponse>(
                `${API_URL}/kyc/customer/draft`,
                getAuthHeaders()
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch KYC draft"
            );
        }
    }
);

const initialState: KycState = {
    kycData: null,
    globalKycData: null,
    kycDraft: null,
    loading: false,
    error: null,
    success: false,
    message: null,
    uploading: false,
    myPayments: {
        data: [],
        pagination: null,
        loading: false,
        error: null,
        success: false,
    },
};

const customerKycSlice = createSlice({
    name: "customerKyc",
    initialState,
    reducers: {
        clearKycState: (state) => {
            state.loading = false;
            state.error = null;
            state.success = false;
            state.message = null;
            state.uploading = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // Upload Document
            .addCase(uploadKycDocument.pending, (state) => {
                state.uploading = true;
                state.error = null;
            })
            .addCase(uploadKycDocument.fulfilled, (state) => {
                state.uploading = false;
            })
            .addCase(uploadKycDocument.rejected, (state, action) => {
                state.uploading = false;
                state.error = action.payload as string;
            })

            // Submit Customer KYC
            .addCase(submitCustomerKyc.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(
                submitCustomerKyc.fulfilled,
                (state, action: PayloadAction<any>) => {
                    const data = action.payload?.data || action.payload;
                    const redirectUrl = action.payload?.redirectUrl || data?.redirectUrl;
                    state.loading = false;
                    state.success = true;
                    if (!redirectUrl) {
                        state.kycData = data;
                    }
                    state.message = redirectUrl ? "Redirecting to payment..." : "KYC details submitted successfully";
                }
            )
            .addCase(submitCustomerKyc.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.success = false;
            })

            // Submit Bank Details
            .addCase(submitBankDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(
                submitBankDetails.fulfilled,
                (state, action: PayloadAction<any>) => {
                    const data = action.payload?.data || action.payload;
                    const redirectUrl = action.payload?.redirectUrl || data?.redirectUrl;
                    state.loading = false;
                    state.success = true;
                    if (!redirectUrl) {
                        state.kycData = data;
                    }
                    state.message = redirectUrl ? "Redirecting to payment..." : "Bank details submitted successfully";
                }
            )
            .addCase(submitBankDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.success = false;
            })

            // Refresh Payment Status
            .addCase(refreshKycPaymentStatus.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(refreshKycPaymentStatus.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                // state.message = `Payment status refreshed: ${action.payload.data.status}`;
            })
            .addCase(refreshKycPaymentStatus.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Submit Unified KYC
            .addCase(submitUnifiedKyc.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(
                submitUnifiedKyc.fulfilled,
                (state, action: PayloadAction<any>) => {
                    const data = action.payload?.data || action.payload;
                    const redirectUrl = action.payload?.redirectUrl || data?.redirectUrl;
                    state.loading = false;
                    state.success = true;
                    if (!redirectUrl) {
                        state.kycData = data;
                    }
                    state.message = redirectUrl ? "Redirecting to payment..." : "KYC details submitted successfully";
                }
            )
            .addCase(submitUnifiedKyc.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.success = false;
            })

            // Submit Nominee Details
            .addCase(submitNomineeDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(
                submitNomineeDetails.fulfilled,
                (state, action: PayloadAction<KycResponseData>) => {
                    state.loading = false;
                    state.success = true;
                    // Java returns only the nominee object — merge into the existing read model.
                    state.kycData = state.kycData
                        ? { ...state.kycData, nominee: action.payload as any }
                        : action.payload;
                    state.message = "Nominee details submitted successfully";
                }
            )
            .addCase(submitNomineeDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.success = false;
            })

            // Get KYC Details
            .addCase(getKycDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                getKycDetails.fulfilled,
                (state, action: PayloadAction<KycResponseData>) => {
                    state.loading = false;
                    state.kycData = action.payload;
                }
            )
            .addCase(getKycDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Global KYC Extra Reducers
            .addCase(submitGlobalCustomerKyc.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(submitGlobalCustomerKyc.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.globalKycData = action.payload;
                state.message = "Global KYC submitted successfully";
            })
            .addCase(submitGlobalCustomerKyc.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            .addCase(submitGlobalBankDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(submitGlobalBankDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.globalKycData = action.payload;
                state.message = "Global Bank details submitted successfully";
            })
            .addCase(submitGlobalBankDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            .addCase(submitGlobalNomineeDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(submitGlobalNomineeDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.globalKycData = action.payload;
                state.message = "Global Nominee details submitted successfully";
            })
            .addCase(submitGlobalNomineeDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            .addCase(getGlobalKycDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getGlobalKycDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.globalKycData = action.payload;
            })
            .addCase(getGlobalKycDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // KYC Draft
            .addCase(saveKycDraft.fulfilled, (state, action) => {
                state.kycDraft = action.payload?.data || null;
                state.message = "Draft saved successfully";
            })
            .addCase(saveKycDraft.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            .addCase(getKycDraft.fulfilled, (state, action) => {
                state.kycDraft = action.payload?.data || null;
            })

            // My Payments
            .addCase(getMyPayments.pending, (state) => {
                state.myPayments.loading = true;
                state.myPayments.error = null;
                state.myPayments.success = false;
            })
            .addCase(
                getMyPayments.fulfilled,
                (state, action: PayloadAction<MyPaymentsResponse>) => {
                    state.myPayments.loading = false;
                    state.myPayments.success = true;
                    state.myPayments.data = action.payload.data;
                    state.myPayments.pagination = {
                        total: action.payload.total,
                        page: action.payload.page,
                        limit: action.payload.limit,
                        totalPages: action.payload.totalPages,
                    };
                }
            )
            .addCase(getMyPayments.rejected, (state, action) => {
                state.myPayments.loading = false;
                state.myPayments.error = action.payload as string;
            });
    },
});

export const { clearKycState } = customerKycSlice.actions;
export default customerKycSlice.reducer;
